/**
 * The one place the shell keeps the loaded save document.
 *
 * `SHELL-001`/`PROG-020`: read once, before anything that depends on it
 * renders. `Boot.tsx` is the only thing that calls `startBoot`; everything
 * else -- today, `Play.tsx`'s backgrounding handler (`PROG-005`) -- reads
 * `bootState` and calls `persist`.
 *
 * A signal rather than component state (`useState` in `Boot.tsx`) because the
 * document has to be readable from screens `Boot` does not render directly --
 * `Play` is several route levels below it through `<Router>`, and threading it
 * down as a prop means every route wrapper in `App.tsx` grows a parameter it
 * only forwards. ADR-0005 picks signals for exactly this shape of problem.
 */

import { signal } from '@preact/signals';

import type { InProgress, SaveDocument } from '@contracts/save';
import type { LoadNotice } from '@platform/storage';
import { saveStore } from '@platform/storage';

export type BootState =
  { status: 'loading' } | { status: 'ready'; document: SaveDocument; notice: LoadNotice | null };

export const bootState = signal<BootState>({ status: 'loading' });

let started = false;

/**
 * `PROG-007`/`PROG-020`. Idempotent so `Boot.tsx` can call it from an effect
 * without tracking on its own whether boot has already happened -- `saveStore`
 * already memoises the underlying read, but `bootState` still needs exactly
 * one writer, or a second call (e.g. a remount during a test) would overwrite
 * a document a caller has since mutated via `persist`.
 */
export function startBoot(): void {
  if (started) return;
  started = true;
  void saveStore.load().then((result) => {
    bootState.value = { status: 'ready', document: result.document, notice: result.notice };
  });
}

/**
 * The loaded document, for a reader that only needs to look, not write.
 *
 * Throws before boot has finished rather than returning `undefined`, because
 * every caller of this today (`Picker.tsx`'s tiles) only ever renders under
 * `Boot.tsx`'s gate, which does not mount `App` -- and so cannot mount a
 * picker tile -- until `bootState` is `ready`. A caller reached outside that
 * gate is a real bug (a test rendering `Picker` on its own, say), and a wrong
 * "0 of 0 complete" on every tile would hide it rather than fail loudly.
 */
export function currentDocument(): SaveDocument {
  const state = bootState.value;
  if (state.status !== 'ready') {
    throw new Error('currentDocument() called before boot finished');
  }
  return state.document;
}

/**
 * Applies `mutate` to the in-memory document, updates `bootState` so every
 * reader sees the change, and writes it back immediately.
 *
 * Immediate (`saveStore.save`) rather than coalesced (`saveStore.scheduleSave`)
 * on purpose: today's one caller is `Play.tsx`'s backgrounding handler
 * (`PROG-005`), which is itself one of the two moments ADR-0007 names as a
 * required flush point -- a debounce window is exactly the bytes that would be
 * lost if the tab is killed a second later.
 *
 * A no-op before boot finishes or against an unwritable (`too-new`, `PROG-024`)
 * load: there is nothing to mutate yet in the first case, and nothing that may
 * be written in the second.
 */
export function persist(mutate: (document: SaveDocument) => SaveDocument): Promise<void> {
  const state = bootState.value;
  if (state.status !== 'ready') return Promise.resolve();
  const next = mutate(state.document);
  bootState.value = { ...state, document: next };
  return saveStore.save(next);
}

/**
 * `PROG-065`, ADR-0012. Call alongside `persist`, not instead of it, from a
 * teardown handler specifically -- `saveStore`'s own doc comment on
 * `persistInProgressSync` has the reasoning. Does not touch `bootState`:
 * unlike `persist`, this is not a claim about what the document currently
 * is, only a synchronous relay for one write that `save()`'s own async path
 * might not survive to finish -- `saveStore.load()`'s next call is what
 * turns it into a real, in-memory document, if it turns out to be needed.
 */
export function persistInProgressSync(inProgress: InProgress): void {
  saveStore.persistInProgressSync(inProgress);
}
